import { supabase } from "@/integrations/supabase/client";
import { enqueueTask } from "@/lib/outboxQueue";

export type AppraisalType = "collection" | "delivery";

export interface TriggerAppraisalEmailSendParams {
  appraisalId: string;
  jobId: string;
  /** Optional for callers that don't have it (e.g. print window helpers). */
  appraisalType?: AppraisalType;
  customerEmail?: string;
  ccEmail?: string;
  /** Bypass server-side "already sending" guard for client-side preflight instrumentation. */
  force?: boolean;
  /** Optional string for log attribution across clients. */
  source?: string;
  /** Company ID for queue tracking */
  companyId?: string;
  /** User ID for queue tracking */
  userId?: string;
}

export interface AppraisalEmailResponse {
  ok: boolean;
  messageId?: string | null;
  to?: string;
  jobId?: string;
  appraisalId?: string;
  appraisalType?: string;
  error?: string;
  details?: string;
  skipped?: boolean;
  message?: string;
}

type TriggerAppraisalEmailSendResult = {
  data: AppraisalEmailResponse | null;
  error: string | null;
  status: number | null;
  queued?: boolean;
};

/**
 * Client-side trigger that:
 * 1) Attempts to send the email immediately via edge function
 * 2) If backend is offline, enqueues the task for later processing
 * 3) Updates the appraisal row to track status
 */
export async function triggerAppraisalEmailSend(
  params: TriggerAppraisalEmailSendParams,
): Promise<TriggerAppraisalEmailSendResult> {
  const {
    appraisalId,
    jobId,
    appraisalType,
    customerEmail,
    ccEmail,
    force = true,
    source,
    companyId,
    userId,
  } = params;

  const dev = import.meta.env.DEV;
  const emailType = appraisalType ? (appraisalType === "collection" ? "POC" : "POD") : undefined;

  if (dev) {
    console.debug("[appraisal-email] trigger:start", {
      appraisalId,
      jobId,
      appraisalType: emailType,
      customerEmail,
      ccEmail,
      force,
      source,
    });
  }

  // Preflight DB audit (so we can see SENDING even if function never runs)
  try {
    const { error: auditError } = await supabase
      .from("vehicle_appraisals")
      .update({
        email_status: "sending",
        email_error: null,
        email_recipient: customerEmail ?? null,
      })
      .eq("id", appraisalId);

    if (dev) {
      console.debug("[appraisal-email] trigger:db_preflight", {
        appraisalId,
        ok: !auditError,
        error: auditError?.message,
      });
    }
  } catch (e) {
    if (dev) console.debug("[appraisal-email] trigger:db_preflight_exception", e);
    // Intentionally continue: we still want to attempt the send.
  }

  // Try to send immediately via edge function
  try {
    const response = await supabase.functions.invoke<AppraisalEmailResponse>("send-appraisal-email", {
      body: {
        appraisalId,
        jobId,
        customerEmail,
        ccEmail,
        force,
        source,
      },
    });

    // Supabase JS attaches status on error.context.status (not guaranteed).
    const status = (response as any)?.error?.context?.status ?? (response.error ? null : 200);

    // The edge function always returns JSON with ok/error even on non-2xx.
    let parsedData: AppraisalEmailResponse | null = null;
    let errorMessage: string | null = null;

    if (response.data) {
      parsedData = response.data as AppraisalEmailResponse;
      if (parsedData && !parsedData.ok && parsedData.error) {
        errorMessage = parsedData.error;
      }
    } else if (response.error) {
      // Supabase wraps the body in context for non-2xx
      const ctx = (response.error as any)?.context;
      if (ctx?.body) {
        try {
          parsedData = JSON.parse(ctx.body) as AppraisalEmailResponse;
          errorMessage = parsedData?.error || response.error.message;
        } catch {
          errorMessage = response.error.message;
        }
      } else {
        errorMessage = response.error.message;
      }
    }

    if (dev) {
      console.debug("[appraisal-email] trigger:edge_response", {
        appraisalId,
        jobId,
        appraisalType: emailType,
        status,
        data: parsedData,
        error: errorMessage,
      });
    }

    return {
      data: parsedData,
      error: errorMessage,
      status,
      queued: false,
    };
  } catch (err: any) {
    // Edge function call failed (likely offline or 404)
    console.warn("[appraisal-email] Edge function failed, enqueueing task:", err.message);

    // Enqueue for later processing
    const taskId = await enqueueTask({
      taskType: "SEND_APPRAISAL_EMAIL",
      appraisalId,
      jobId,
      appraisalType,
      payload: {
        customerEmail,
        ccEmail,
        force,
        source: source || "queued_from_failure",
      },
      companyId,
      userId,
    });

    // Update appraisal status to show it's queued
    await supabase
      .from("vehicle_appraisals")
      .update({
        email_status: "pending",
        email_error: "Queued for later - backend temporarily unavailable",
      })
      .eq("id", appraisalId);

    if (dev) {
      console.debug("[appraisal-email] trigger:queued", { taskId, appraisalId });
    }

    return {
      data: {
        ok: true,
        message: "Queued for later processing",
        appraisalId,
        jobId,
      },
      error: null,
      status: null,
      queued: true,
    };
  }
}
